import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { ArrowLeft, Eye } from 'lucide-react';
import './Tavern.css';

// Mock data for categories - in a real app this would come from API
const MOCK_CATEGORIES = [
  {
    id: '1',
    name: 'Announcements',
    description: 'Official announcements from the ChoiceRealms team',
  },
  {
    id: '2',
    name: 'General Discussion',
    description: 'Talk about anything related to ChoiceRealms',
  },
  {
    id: '3',
    name: 'Character Builds',
    description: 'Share and discuss character builds and strategies',
  },
  {
    id: '4',
    name: 'Game Modules',
    description: 'Discuss official and community game modules',
  },
  {
    id: '5',
    name: 'Technical Support',
    description: 'Get help with technical issues',
  }
];

const TavernThreadForm = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || '');
  const [showPreview, setShowPreview] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const authenticatedUser = await getCurrentUser();
      setUser(authenticatedUser);
    } catch (err) {
      console.log('Error getting current user:', err);
      setUser(null);
      setError('You must be logged in to create a thread');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validation
    if (title.trim().length < 5) {
      setError('Title must be at least 5 characters long');
      return;
    }
    if (content.trim().length < 20) {
      setError('Content must be at least 20 characters long');
      return;
    }
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    // Clear any previous errors
    setError('');

    // In a real application, this would be an API call to create the thread
    console.log({
      title,
      content,
      categoryId: selectedCategory,
      author: user?.username
    });

    // Simulate successful creation and redirect
    navigate(`/tavern/category/${selectedCategory}`);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="tavern-container">
        <div className="thread-detail">
          <div className="thread-detail__header">
            <h1 className="thread-detail__title">Authentication Required</h1>
          </div>
          <div className="thread-detail__content">
            <p>You must be logged in to create a new thread.</p>
            <div className="tavern-actions" style={{ marginTop: '1rem' }}>
              <button 
                className="tavern-action-button"
                onClick={() => navigate('/auth')}
              >
                Sign In
              </button>
              <button 
                className="tavern-action-button"
                onClick={() => navigate('/tavern')}
              >
                Back to Tavern
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedCategoryName = MOCK_CATEGORIES.find(cat => cat.id === selectedCategory)?.name || '';

  return (
    <div className="tavern-container">
      <header className="tavern-header">
        <div className="tavern-header__content">
          <h1 className="tavern-title">Create New Thread</h1>
          <p className="tavern-description">
            Share your thoughts, questions, or ideas with the ChoiceRealms community
          </p>
        </div>
      </header>

      <main>
        <form className="post-reply-form" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message" style={{ color: 'var(--color-danger)', marginBottom: 'var(--spacing-md)' }}>
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label" htmlFor="category">Category</label>
            <select 
              id="category"
              className="form-input"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              required
            >
              <option value="">-- Select a Category --</option>
              {MOCK_CATEGORIES.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="form-label" htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              className="form-input"
              placeholder="Give your thread a descriptive title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              minLength="5"
              maxLength="100"
            />
          </div>
          
          {showPreview && (
            <div className="post-reply-form__preview">
              <h3 className="post-reply-form__preview-title">
                Preview: {title || 'Thread Title'} 
                {selectedCategoryName && ` in ${selectedCategoryName}`}
              </h3>
              <div className="post-reply-form__preview-content">
                {content || 'Thread content will appear here...'}
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label className="form-label" htmlFor="content">Content</label>
            <textarea 
              id="content"
              className="post-reply-form__content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your thread content here..."
              required
              minLength="20"
            />
          </div>
          
          <div className="post-reply-form__actions">
            <button 
              type="button" 
              className="tavern-action-button"
              onClick={() => navigate('/tavern')}
            >
              <ArrowLeft size={16} />
              Cancel
            </button>
            <button 
              type="button" 
              className="tavern-action-button"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye size={16} />
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button type="submit" className="tavern-action-button">
              Create Thread
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default TavernThreadForm;